import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import SearchOverlay from "@/components/SearchOverlay";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/news", label: "News" },
  { to: "/magazine", label: "Magazine" },
  { to: "/articles", label: "Articles" },
  { to: "/interviews", label: "Interviews" },
  { to: "/get-featured", label: "Get Featured" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-500 ${
          scrolled ? "glass-light py-2.5" : "bg-paper/60 backdrop-blur-md py-4 border-b border-ink-line"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo-link" aria-label="World Visionary Times home" className="flex-shrink-0">
            <Logo size={scrolled ? "sm" : "md"} />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] tracking-wide transition-colors ${
                    isActive ? "text-ink font-medium active" : "text-ink-soft hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-search-button"
              onClick={() => setSearchOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-ink-line hover:border-ink text-ink-soft hover:text-ink transition-all"
              aria-label="Open search"
            >
              <Search size={16} strokeWidth={1.4} />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-ink-line text-ink-soft"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X size={16} strokeWidth={1.4} /> : <Menu size={16} strokeWidth={1.4} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden fixed inset-0 z-[55] bg-paper/98 backdrop-blur-xl pt-24 px-6"
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.05 + i * 0.04 } }}
                >
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-baseline gap-4 py-5 border-b border-ink-line font-display text-3xl font-normal text-ink hover:text-gold transition-colors"
                  >
                    <span className="text-ink-muted font-mono-editorial text-xs">0{i + 1}</span>
                    <span>{n.label}</span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      {/* Spacer to prevent content jump under fixed navbar */}
      <div aria-hidden="true" className="h-[76px]" />
    </>
  );
};

export default Navbar;
